import React, { useState } from "react";
import { confirmAlert } from 'components/common';
import { useDispatch } from "react-redux";
import { MdSecurity, MdFormatListBulleted } from "react-icons/md";
import { FaPlus, FaEdit, FaTrashAlt } from "react-icons/fa";
import CrmListPage from "components/app/crm/shared/CrmListPage";
import PermissionForm from "components/app/authorization/permission/PermissionForm";
import { getAllPermissions, exportPermissions, deletePermission } from "redux/authorization/permissionSlice";
import { PERMISSION_PARENT_GROUPS } from "app_config";

const columns = [
  { headerName: "ID#",          field: "id",            minWidth: 90,  sortable: false },
  { headerName: "Subject class", field: "subject_class", minWidth: 220, sortable: false },
  { headerName: "Action",       field: "action",        minWidth: 140, sortable: false },
  { headerName: "Description",  field: "description",   minWidth: 280, sortable: false },
  { headerName: "Code",        field: "code",          minWidth: 110, sortable: false },
  { headerName: "Group",       field: "group_name",    minWidth: 160, sortable: false },
  { headerName: "Parent group", field: "parent_group",  minWidth: 150, sortable: false }
];

const ADVANCED_FIELDS = [
  { key: "subject_class", label: "Subject class", type: "text",        paramKey: "subject_class_cont", tagName: "Subject" },
  { key: "action",        label: "Action",        type: "text",        paramKey: "action_cont",        tagName: "Action" },
  { key: "description",   label: "Description",   type: "text",        paramKey: "description_cont",    tagName: "Desc" },
  { key: "parent_group",  label: "Parent group",  type: "multiselect", options: PERMISSION_PARENT_GROUPS.map((g) => ({ value: g, label: g })), paramKey: "parent_group_in", tagName: "Group" }
];

const TABS = [{ key: "all", label: "All", Icon: MdFormatListBulleted, thunk: getAllPermissions, dataKey: "allData" }];

const PermissionListPage = () => {
  const dispatch = useDispatch();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const openNew = () => { setEditing(null); setShowForm(true); };
  const openEdit = (permission) => { setEditing(permission); setShowForm(true); };

  const handleDelete = (rows, reload) => {
    confirmAlert({
      message: `Delete ${rows.length} permission${rows.length > 1 ? "s" : ""}? This cannot be undone.`,
      onConfirm: async () => {
        await dispatch(deletePermission(rows.map((r) => r.id)));
        reload?.();
      }
    });
  };

  // "New Permission" button next to Reload / Export.
  const headerExtra = (
    <button
      type="button"
      onClick={openNew}
      className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-primary-600 tw-bg-primary-600 tw-text-white hover:tw-bg-primary-700 tw-text-sm tw-font-medium tw-cursor-pointer"
    >
      <FaPlus size={11} /> New Permission
    </button>
  );

  // Edit (single selection) / Delete (any selection) — shown when rows are checked.
  const renderSelectionActions = (rows, reload) => (
    <div className="tw-flex tw-items-center tw-gap-2 tw-mr-1">
      <span className="tw-text-xs tw-text-slate-500 tw-font-medium tw-whitespace-nowrap">{rows.length} selected</span>
      {rows.length === 1 && (
        <button
          type="button"
          onClick={() => openEdit(rows[0])}
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-slate-300 tw-bg-white tw-text-slate-700 hover:tw-bg-slate-50 tw-text-sm tw-cursor-pointer"
        >
          <FaEdit size={12} /> Edit
        </button>
      )}
      <button
        type="button"
        onClick={() => handleDelete(rows, reload)}
        className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-red-300 tw-bg-red-50 tw-text-red-600 hover:tw-bg-red-100 tw-text-sm tw-font-medium tw-cursor-pointer"
      >
        <FaTrashAlt size={12} /> Delete
      </button>
    </div>
  );

  return (
    <>
      <CrmListPage
        title="Permissions"
        subtitle="System permission catalogue."
        Icon={MdSecurity}
        reducerSelector={(s) => s.authorization_permission}
        tabs={TABS}
        columns={columns}
        searchParamKey="subject_class_or_action_or_description_or_parent_group_cont"
        searchPlaceholder="Subject, action, description..."
        advancedFields={ADVANCED_FIELDS}
        advancedTitle="Filters for permissions"
        exportThunk={exportPermissions}
        headerExtra={headerExtra}
        enableSelection
        renderSelectionActions={renderSelectionActions}
      />

      <PermissionForm
        showModal={showForm}
        setshowModal={setShowForm}
        title={editing ? "Edit Permission" : "New Permission"}
        permission={editing}
      />
    </>
  );
};

export default PermissionListPage;
